import { ChangeEvent, DragEvent, FC, useRef, useState } from "react";
import { infoIcon, uploadBlackIcon } from "utils/constant";

interface FileUploadProps {
  label?: string;
  fileTypes?: string;
  required?: boolean;
  multiple?: boolean;
  info?: string;
  onChange: (files: File[] | null) => void;
}

const FileUploadMultiple: FC<FileUploadProps> = ({
  label = "Attachment",
  fileTypes = "image/*",
  required = false,
  multiple = false,
  info = "",
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    setFilesToState(files);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setFilesToState(files);
  };

  const setFilesToState = (files: File[]) => {
    // Optionally, add file type validation here
    setError(null);
    setSelectedFiles(files);
    onChange(files.length ? files : null);
  };

  const clearFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setError(null);
    onChange(newFiles.length ? newFiles : null);
    if (newFiles.length === 0 && inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex flex-col w-full">
      {/* Label */}
      <label
        htmlFor="file-upload"
        className="flex align-top text-gray-700 font-medium mb-2 gap-2"
      >
        {infoIcon}
        {label}
        {required && (
          <span className="text-red-500" title="This field is required">
            *
          </span>
        )}
      </label>

      {/* Show selected files */}
      {selectedFiles.length > 0 ? (
        <div className="flex flex-col gap-2">
          {selectedFiles.map((file, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-4 py-2"
            >
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => clearFile(idx)}
                className="ml-4 text-gray-400 hover:text-gray-600"
                aria-label="Remove selected file"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : (
        /* Dropzone */
        <div
          className="
            group relative flex cursor-pointer flex-col items-center justify-center
            rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-12
            hover:bg-gray-100 transition
          "
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {uploadBlackIcon}
          <p className="text-gray-500 group-hover:text-gray-600">
            Upload {multiple ? "files" : "a file"} here
          </p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        id="file-upload"
        type="file"
        accept={fileTypes}
        ref={inputRef}
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
      />

      {/* Hint */}
      {info && <p className="mt-1 text-xs text-gray-500">{info}</p>}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default FileUploadMultiple;
